import {useEffect, useState} from "react";
import {BaseAlert, BaseButton, BaseCard} from "@/components";
import {useGameLaunch} from "./useGameLaunch";

export interface GameLaunchPageProps {
  provider: string;
  gameId: string;
  onBack?: () => void;
}

const useIsMobile = (): boolean => {
  const [isMobile, setIsMobile] = useState(() => (typeof window !== "undefined" ? window.innerWidth <= 768 : false));
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return isMobile;
};

const GameLaunchPage = ({provider, gameId, onBack}: GameLaunchPageProps) => {
  const isMobile = useIsMobile();
  const {gameUrl, isLoading, isError, error, refetch} = useGameLaunch({provider, gameId, isMobile});
  const [fullscreen, setFullscreen] = useState(false);

  useEffect(() => {
    setFullscreen(isMobile);
  }, [isMobile]);

  if (isLoading) {
    return (
      <section className="mx-auto flex w-full max-w-2xl flex-col gap-4 px-4 py-8">
        <BaseCard
          variant="surface"
          padding="lg"
        >
          <p className="text-center text-[var(--font-body-lg)] text-[var(--color-text-secondary)]">Cargando juego...</p>
        </BaseCard>
      </section>
    );
  }

  if (isError || !gameUrl) {
    return (
      <section className="mx-auto flex w-full max-w-2xl flex-col gap-4 px-4 py-8">
        <BaseAlert
          variant="danger"
          title="No pudimos iniciar el juego"
        >
          <p className="mb-3">{error?.message ?? "Intenta nuevamente en unos segundos."}</p>
          <div className="flex flex-wrap gap-2">
            <BaseButton
              variant="primary"
              size="sm"
              onClick={() => refetch()}
            >
              Reintentar
            </BaseButton>
            {onBack ? (
              <BaseButton
                variant="secondary"
                size="sm"
                onClick={onBack}
              >
                Volver
              </BaseButton>
            ) : null}
          </div>
        </BaseAlert>
      </section>
    );
  }

  if (fullscreen) {
    return (
      <div className="fixed inset-0 z-[var(--z-modal)] flex h-screen w-screen items-center justify-center bg-black">
        <button
          type="button"
          onClick={() => (onBack ? onBack() : setFullscreen(false))}
          className="absolute right-4 top-4 z-10 rounded-full bg-[var(--color-surface)] px-4 py-2 text-[var(--font-body)] font-semibold text-[var(--color-text)] shadow-lg"
          aria-label="Salir del juego"
        >
          ✕ Salir
        </button>
        <iframe
          src={gameUrl}
          title={`${provider} ${gameId}`}
          allow="autoplay; fullscreen; payment"
          allowFullScreen
          className="h-full w-full border-0"
        />
      </div>
    );
  }

  return (
    <section className="mx-auto flex w-full max-w-5xl flex-col gap-4 px-4 py-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-col">
          <p className="text-[var(--font-body)] uppercase tracking-wider text-[var(--color-text-tertiary)]">
            {provider}
          </p>
          <h1 className="text-[var(--font-h2)] font-bold text-[var(--color-text)]">{gameId}</h1>
        </div>
        <div className="flex gap-2">
          <BaseButton
            variant="ghost"
            size="sm"
            onClick={() => setFullscreen(true)}
          >
            Pantalla completa
          </BaseButton>
          {onBack ? (
            <BaseButton
              variant="secondary"
              size="sm"
              onClick={onBack}
            >
              Salir
            </BaseButton>
          ) : null}
        </div>
      </header>
      <div className="aspect-video w-full overflow-hidden rounded-xl bg-black shadow-lg">
        <iframe
          src={gameUrl}
          title={`${provider} ${gameId}`}
          allow="autoplay; fullscreen; payment"
          allowFullScreen
          className="h-full w-full border-0"
        />
      </div>
    </section>
  );
};

export default GameLaunchPage;
